import { useMemo } from 'react'
import useBitsStore from '../store/bitsStore'
import useSettingsStore from '../store/settingsStore'
import '../styles/BitsVariants.css'

// Rain drops generator
function RainEffect() {
  const drops = useMemo(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 0.5 + Math.random() * 0.4
    })), []
  )

  return (
    <div className="bits-effect-rain">
      {drops.map(drop => (
        <div
          key={drop.id}
          className="rain-drop"
          style={{
            left: `${drop.left}%`,
            animationDelay: `${drop.delay}s`,
            animationDuration: `${drop.duration}s`
          }}
        />
      ))}
    </div>
  )
}

// Fireworks particles
function FireworksEffect() {
  const particles = useMemo(() => {
    const bursts = []
    for (let b = 0; b < 3; b++) {
      const cx = 20 + Math.random() * 60
      const cy = 20 + Math.random() * 40
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2
        const distance = 30 + Math.random() * 40
        bursts.push({
          id: `${b}-${i}`,
          left: cx,
          top: cy,
          tx: Math.cos(angle) * distance,
          ty: Math.sin(angle) * distance,
          color: ['#ff6b6b', '#ffd93d', '#6bcf7f', '#74b9ff', '#a29bfe', '#fd79a8'][i % 6],
          delay: b * 0.5
        })
      }
    }
    return bursts
  }, [])

  return (
    <div className="bits-effect-fireworks">
      {particles.map(p => (
        <div
          key={p.id}
          className="firework-particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            backgroundColor: p.color,
            '--tx': `${p.tx}px`,
            '--ty': `${p.ty}px`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
    </div>
  )
}

// Butterfly swarm
function SwarmEffect() {
  const butterflies = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: 10 + Math.random() * 80,
      top: 10 + Math.random() * 60,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2
    })), []
  )

  return (
    <div className="bits-effect-swarm">
      {butterflies.map(b => (
        <span
          key={b.id}
          className="swarm-butterfly"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.duration}s`
          }}
        >🦋</span>
      ))}
    </div>
  )
}

// Mega Bloom burst
function MegaBloomEffect() {
  const petals = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: 30 + Math.random() * 40,
      top: 20 + Math.random() * 40,
      delay: Math.random() * 0.6,
      duration: 1.2 + Math.random() * 0.8,
      rotation: Math.random() * 360,
      symbol: ['🌸', '🌺', '✨', '💮', '🏵️'][i % 5]
    })), []
  )

  return (
    <div className="bits-effect-mega-bloom">
      {petals.map(p => (
        <span
          key={p.id}
          className="bloom-petal"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--rotation': `${p.rotation}deg`
          }}
        >{p.symbol}</span>
      ))}
    </div>
  )
}

export function BitsEffects() {
  const settings = useSettingsStore((state) => state.settings)
  const activeEffects = useBitsStore((state) => state.activeEffects)
  const bitsNotifications = useBitsStore((state) => state.bitsNotifications)

  if (!settings.enableBitsFeatures) return null

  const hasEarthquake = activeEffects.some(e => e.effectId === 'earthquake')

  return (
    <>
      {/* Active visual effects */}
      {activeEffects.map(effect => {
        switch (effect.effectId) {
          case 'rain': return <RainEffect key={effect.id} />
          case 'fireworks': return <FireworksEffect key={effect.id} />
          case 'swarm': return <SwarmEffect key={effect.id} />
          case 'mega_bloom': return <MegaBloomEffect key={effect.id} />
          case 'earthquake': return null // handled via CSS injection below
          default: return null
        }
      })}

      {/* Earthquake — inject CSS that shakes the terrarium container */}
      {hasEarthquake && (
        <style>{`.terrarium-container, .wide-terrarium-container { animation: earthquakeShake 0.3s ease-in-out infinite; }`}</style>
      )}

      {/* Bits notifications */}
      {settings.showBitsNotifications && bitsNotifications.length > 0 && (
        <div className="bits-notifications">
          {bitsNotifications.map(notification => (
            <div key={notification.id} className="bits-notification">
              <span className="bits-notification-icon">{notification.icon}</span>
              <span className="bits-notification-text">{notification.text}</span>
              {notification.bits > 0 && (
                <span className="bits-notification-amount">+{notification.bits}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export default BitsEffects
